import { errorAlert } from '@/components/others/ToastGroup';
import {
  FUNGIBLE_STAKE_CONFIG_SEED,
  FUNGIBLE_VAULT_SEED,
  MEME_STAKE_INFO_SEED,
  MEME_STAKE_VAULT_SEED,
  MODE_COIN,
  SEED_CURVE_VAULT,
  SEED_GLOBAL,
  SPL_DECIMAL,
  TOKEN_CREATOR_SEED,
} from '@/config';
import { SimpleSnapshotContractQueryClient as AgentsLandSnapshotContractQueryClient } from '@/sdk/oraiAgentSdk';
import {
  rayBuyTx,
  raySellTx,
  simulateSwapOnRaydium,
} from '@/utils/raydiumSwap/raydiumSwap';
import { LaunchDataInfo, MetadataInfo, StakeInfo } from '@/utils/types';
import {
  calculateMarketCap,
  calculateTokenPrice,
  toBN,
  toPublicKey,
} from '@/utils/util';
import * as anchor from '@coral-xyz/anchor';
import { BN, Program } from '@coral-xyz/anchor';
import { CosmWasmClient } from '@cosmjs/cosmwasm-stargate';
import { Metaplex } from '@metaplex-foundation/js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { WalletContextState } from '@solana/wallet-adapter-react';
import {
  ComputeBudgetProgram,
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  Transaction,
} from '@solana/web3.js';
import BigNumber from 'bignumber.js';
import { ALL_CONFIGS } from '../config';
import { fetchJSONDataFromUrl } from './logListeners/utils';
import { PublicLaunchpad } from './memeLaunchpad/public_launchpad';
import idl from './memeLaunchpad/public_launchpad.json';
import { SEED_BONDING_CURVE, SEED_CONFIG } from './seed';
import { handleTransaction } from './utils';

export type ConfigLaunchPad = anchor.IdlAccounts<PublicLaunchpad>['config'];

export const commitmentLevel = 'confirmed';
export const TOKEN_RESERVES = 1_000_000_000_000_000;
export const LAMPORT_RESERVES = 1_000_000_000;

export const pumpProgramId = new PublicKey(idl.address);
export const pumpProgramInterface = idl as PublicLaunchpad;

let globalCTOLaunchpadConfig: ConfigLaunchPad;
let globalDEGENLaunchpadConfig: ConfigLaunchPad;
export class Web3MemeLaunchPadProgramInteraction {
  constructor() {}

  async isCreatorWhitelisted(creator: PublicKey) {
    const provider = anchor.getProvider();
    const program = new Program(
      pumpProgramInterface as PublicLaunchpad,
      provider,
    ) as Program<PublicLaunchpad>;

    const [whitelistCreatorPda, _] = PublicKey.findProgramAddressSync(
      [Buffer.from(TOKEN_CREATOR_SEED), creator.toBytes()],
      program.programId,
    );
    // if account info not found -> not whitelisted
    try {
      const creatorWhitelistData = await program.account.tokenCreator.fetch(
        whitelistCreatorPda,
      );

      console.log('whitelist data: ', creatorWhitelistData);
      return creatorWhitelistData.whitelisted;
    } catch (error) {
      console.log('Error decoding creator whitelist account: ', error);
      return false;
    }
  }

  private async getLaunchpadGlobalConfig() {
    globalCTOLaunchpadConfig = await this.getCTOLaunchpadGlobalConfig();
    globalDEGENLaunchpadConfig = await this.getDEGENLaunchpadGlobalConfig();

    return {
      globalCTOLaunchpadConfig,
      globalDEGENLaunchpadConfig,
    };
  }

  private async getCTOLaunchpadGlobalConfig() {
    if (!globalCTOLaunchpadConfig) {
      const provider = anchor.getProvider();
      const program = new Program(
        pumpProgramInterface as PublicLaunchpad,
        provider,
      ) as Program<PublicLaunchpad>;

      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG), new BN(MODE_COIN.CTO).toBuffer('le', 8)],
        program.programId,
      );
      const configAccount = await program.account.config.fetch(configPda);
      globalCTOLaunchpadConfig = configAccount;
    }
    return globalCTOLaunchpadConfig;
  }

  private async getDEGENLaunchpadGlobalConfig() {
    if (!globalDEGENLaunchpadConfig) {
      const provider = anchor.getProvider();
      const program = new Program(
        pumpProgramInterface as PublicLaunchpad,
        provider,
      ) as Program<PublicLaunchpad>;

      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG), new BN(MODE_COIN.DEGEN).toBuffer('le', 8)],
        program.programId,
      );
      const configAccount = await program.account.config.fetch(configPda);
      globalDEGENLaunchpadConfig = configAccount;
    }
    return globalDEGENLaunchpadConfig;
  }

  // Send Fee to the Fee destination
  createToken = async (
    wallet: WalletContextState,
    coinData: LaunchDataInfo,
    mintKp: Keypair,
    mode: MODE_COIN,
  ) => {
    const provider = anchor.getProvider();
    const program = new Program(
      pumpProgramInterface as PublicLaunchpad,
      provider,
    ) as Program<PublicLaunchpad>;

    // check the connection
    if (!wallet.publicKey || !provider.connection) {
      errorAlert('Wallet Not Connected');
      console.log('Warning: Wallet not connected');
      return 'WalletError';
    }

    try {
      console.log('coinData--->', coinData);
      // const { globalCTOLaunchpadConfig, globalDEGENLaunchpadConfig } =
      // await this.getLaunchpadGlobalConfig();

      // const configAccount =
      //   mode === MODE_COIN.CTO
      //     ? globalCTOLaunchpadConfig
      //     : globalDEGENLaunchpadConfig;

      const configAccount =
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig();

      const charityTokenAccount = this.getAssociatedTokenAccount(
        configAccount.charityWallet,
        mintKp.publicKey,
      );
      const creatorTokenAccount = this.getAssociatedTokenAccount(
        wallet.publicKey,
        mintKp.publicKey,
      );

      const stakingTokenAccount = this.getAssociatedTokenAccount(
        configAccount.stakingPoolWallet,
        mintKp.publicKey,
      );

      const communityPoolTokenAccount = this.getAssociatedTokenAccount(
        configAccount.communityPoolWallet,
        mintKp.publicKey,
      );
      const [vaultPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CURVE_VAULT), mintKp.publicKey.toBytes()],
        program.programId,
      );

      const vaultTokenAccount = this.getAssociatedTokenAccount(
        vaultPDA,
        mintKp.publicKey,
      );

      const transaction = new Transaction();
      const updateCpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const updateCuIx = ComputeBudgetProgram.setComputeUnitLimit({
        units: 500_000,
      });
      const createIx = await program.methods
        .launch(new BN(mode), coinData.name, coinData.symbol, coinData.uri)
        .accounts({
          creator: wallet.publicKey,
          token: mintKp.publicKey,
        })
        .remainingAccounts([
          {
            isWritable: true,
            isSigner: false,
            pubkey: configAccount.communityPoolWallet,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: configAccount.charityWallet,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: configAccount.stakingPoolWallet,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: vaultTokenAccount,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: charityTokenAccount,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: creatorTokenAccount,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: stakingTokenAccount,
          },
          {
            isWritable: true,
            isSigner: false,
            pubkey: communityPoolTokenAccount,
          },
        ])
        .instruction();

      console.log('createIx', createIx);

      transaction.add(updateCpIx, updateCuIx, createIx);

      if (coinData.presale) {
        const swapIx = await program.methods
          .swap(
            new BN(mode),
            new anchor.BN(coinData.presale * Math.pow(10, 9)),
            0,
            new anchor.BN(0),
          )
          .accounts({
            teamWallet: configAccount.teamWallet,
            user: wallet.publicKey,
            tokenMint: mintKp.publicKey,
          })
          .instruction();
        transaction.add(swapIx);
      }

      transaction.feePayer = wallet.publicKey;
      const blockhash = await provider.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash.blockhash;

      transaction.sign(mintKp);
      console.log('--------------------------------------');
      console.log(transaction);

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        console.log(
          '---- simulate tx',
          await provider.connection.simulateTransaction(signedTx),
        );
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'finalized',
        );
        console.log('Successfully initialized.\n Signature: ', signature);
        return {
          ...coinData,
          token: mintKp.publicKey,
          result: res,
        };
      }
    } catch (error) {
      console.log('--error--', error);
      return false;
    }
  };

  getMaxBondingCurveLimit = async (
    mint: string,
    wallet: WalletContextState,
    mode: MODE_COIN,
  ): Promise<any> => {
    const provider = anchor.getProvider();
    // check the connection
    if (!provider.connection) {
      console.log('Warning: connection not connected');
      return;
    }
    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<PublicLaunchpad>;

    try {
      const configAccount =
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig();

      const platformBuyFee = configAccount.platformBuyFee || 0;
      const curveLimit = configAccount.curveLimit.toNumber();
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), toPublicKey(mint).toBytes()],
        program.programId,
      );
      const curveAccount = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );

      const solReserve = curveAccount.reserveLamport.toNumber();
      const maxSolSwap = curveLimit - solReserve;

      const maxSolSwapIncludeFee = new BigNumber(maxSolSwap)
        .multipliedBy(new BigNumber(platformBuyFee).plus(100))
        .div(100)
        .toNumber();

      return { curveAccount, maxSolSwapIncludeFee };
    } catch (error) {
      console.log('Error in get config curve limit', error);
      return { curveAccount: {}, maxSolSwapIncludeFee: 0 };
    }
  };

  getBondingCurveOfToken = async (mint: string): Promise<any> => {
    const provider = anchor.getProvider();
    // check the connection
    if (!provider.connection) {
      console.log('Warning: connection not connected');
      return;
    }
    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<PublicLaunchpad>;

    try {
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), toPublicKey(mint).toBytes()],
        program.programId,
      );
      const curveInfo = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );

      // console.log('curveInfo', curveInfo);

      return curveInfo;
    } catch (error) {
      console.log('Error in get config curve limit', error);
      return;
    }
  };

  getConfigData = async (
    wallet: WalletContextState,
    mode: MODE_COIN,
  ): Promise<any> => {
    const provider = anchor.getProvider();
    // check the connection
    if (!provider.connection) {
      console.log('Warning: connection not connected');
      return;
    }
    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<PublicLaunchpad>;

    try {
      const configAccount =
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig();

      const platformBuyFee = configAccount.platformBuyFee;
      return platformBuyFee;
    } catch (error) {
      console.log('Error in get config curve limit', error);
      return 0;
    }
  };

  // Swap transaction
  swapTx = async (
    mintAddress: string,
    wallet: WalletContextState,
    amount: string,
    type: number,
    simulateReceive: string,
    slippage: string,
    isParty: boolean,
    mode: MODE_COIN,
  ): Promise<any> => {
    console.log('==============trade swap==============');

    let provider;
    try {
      provider = anchor.getProvider();
      // check the connection
      if (!wallet.publicKey || !provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const mint = toPublicKey(mintAddress);

      const configAccount =
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig();

      const curveLimit = configAccount.curveLimit.toNumber();
      // check launch phase is 'Presale'
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), mint.toBytes()],
        program.programId,
      );

      const curveAccount = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );

      const solReserve = curveAccount.reserveLamport.toNumber();

      const maxSolSwap = curveLimit - solReserve;
      console.log(maxSolSwap, amount);

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const coinDecimal = type === 0 ? 9 : 6;
      const receiveDecimal = type !== 0 ? 9 : 6;
      const addFee = type === 0 ? slippage : Number(slippage) + 1; // fee
      const fmtAmount = new anchor.BN(
        parseFloat(amount) * Math.pow(10, coinDecimal),
      );

      // FIXME: uncomment to check limit curve
      // if (
      //   type === 0 &&
      //   new BigNumber(fmtAmount.toNumber()).isGreaterThan(maxSolSwap)
      // ) {
      //   console.log("Exceeded bonding curve limit");
      //   throw Error("Exceeded bonding curve limit");
      // }

      const minAmount = toBN(simulateReceive)
        .multipliedBy(Math.pow(10, receiveDecimal))
        .multipliedBy(toBN(1).minus(Number(addFee) / 100))
        .toNumber();

      let swapIx = await program.methods
        .swap(new BN(mode), fmtAmount, type, new anchor.BN(minAmount || 0))
        .accounts({
          teamWallet: configAccount.teamWallet,
          user: wallet.publicKey,
          tokenMint: mint,
        })
        .instruction();

      if (isParty) {
        let [stakeConfigPda] = PublicKey.findProgramAddressSync(
          [
            Buffer.from(FUNGIBLE_STAKE_CONFIG_SEED),
            new PublicKey(ALL_CONFIGS.STAKE_CURRENCY_MINT).toBytes(),
          ],
          new PublicKey(ALL_CONFIGS.STAKING_PROGRAM_ID),
        );

        let [vaultPda] = PublicKey.findProgramAddressSync(
          [
            Buffer.from(FUNGIBLE_VAULT_SEED),
            stakeConfigPda.toBytes(),
            toPublicKey(mint).toBytes(),
          ],
          new PublicKey(ALL_CONFIGS.STAKING_PROGRAM_ID),
        );
        let [userStakePda] = PublicKey.findProgramAddressSync(
          [
            Buffer.from('stake_info'),
            vaultPda.toBytes(),
            wallet.publicKey.toBytes(),
          ],
          new PublicKey(ALL_CONFIGS.STAKING_PROGRAM_ID),
        );

        swapIx = await program.methods
          .buyParty(
            new BN(mode),
            fmtAmount,
            type,
            new anchor.BN(minAmount || 0),
          )
          .accounts({
            teamWallet: configAccount.teamWallet,
            user: wallet.publicKey,
            tokenMint: mint,
          })
          .remainingAccounts([
            {
              isWritable: true,
              isSigner: false,
              pubkey: stakeConfigPda,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: new PublicKey(ALL_CONFIGS.STAKE_CURRENCY_MINT),
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: vaultPda,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: userStakePda,
            },
          ])
          .instruction();
      }

      transaction.add(swapIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed', // FIXME: trick lord confirmed / finalized;
        );

        console.log('Successfully initialized.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in swap transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  };

  //Raydium Swap transaction
  raydiumSwapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
    poolKey: string,
    slippage: string,
  ) => {
    let provider;
    try {
      provider = anchor.getProvider();
      // check the connection
      if (!wallet.publicKey || !provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }

      const poolId = new PublicKey(poolKey);
      const coinDecimal = type === 0 ? 9 : 6;
      const fmtAmount = new anchor.BN(
        parseFloat(amount) * Math.pow(10, coinDecimal),
      ).toNumber();
      const transaction = new Transaction();

      let swapTx;
      if (type == 0) {
        swapTx = await rayBuyTx(
          provider.connection,
          mint,
          fmtAmount,
          wallet,
          poolId,
          slippage,
        );
      } else {
        swapTx = await raySellTx(
          provider.connection,
          mint,
          fmtAmount,
          wallet,
          poolId,
          slippage,
        );
      }
      if (swapTx == null) {
        console.log(`Error getting buy transaction`);
        return null;
      }

      // transaction.add(cpIx, cuIx);
      transaction.add(...swapTx.instructions);

      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        // console.log(
        //   "----",
        //   await this.connection.simulateTransaction(signedTx)
        // );
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();
        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );
        console.log('Successfully initialized.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in swap transaction', error, error.error);
      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----raydium', { transaction, result });
        return { transaction, result };
      }
    }
  };

  //Raydium Swap transaction
  simulateRaydiumSwapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
    poolKey: string,
  ) => {
    try {
      const provider = anchor.getProvider();
      if (!poolKey) {
        console.log('Coin not listed');
        return;
      }

      // check the connection
      if (!wallet.publicKey || !provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const poolId = new PublicKey(poolKey);
      const res = await simulateSwapOnRaydium(type, {
        solanaConnection: provider.connection,
        baseMint: mint,
        amount: Number(amount),
        wallet,
        poolId,
      });

      if (res) {
        const { numerator, denominator } = res;

        // return new anchor.BN(denominator).eqn(0)
        //   ? 0
        //   : new anchor.BN(numerator).div(denominator).toNumber();

        // return numerator.toString();
        return res;
      }

      // return "0";
    } catch (error) {
      console.log('Error in simulate swap transaction', error);
    }
  };

  // Swap transaction
  simulateSwapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
    mode: MODE_COIN,
  ): Promise<string> => {
    console.log('========Simulate swap==============');

    const provider = anchor.getProvider();
    // check the connection
    if (!wallet.publicKey || !provider.connection) {
      console.log('Warning: Wallet not connected');
      return;
    }

    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<PublicLaunchpad>;

    try {
      const tx = await program.methods
        .simulateSwap(new BN(mode), new BN(amount), type)
        .accounts({
          tokenMint: mint,
        })
        .view();

      const actualAmountOut = new BN(tx).toString();
      return actualAmountOut;
    } catch (error) {
      console.log('Error in simulate swap transaction', error, error.error);
    }
  };

  getTokenBalance = async (walletAddress: string, tokenMintAddress: string) => {
    try {
      const wallet = new PublicKey(walletAddress);
      const tokenMint = new PublicKey(tokenMintAddress);

      const provider = anchor.getProvider();
      // Fetch the token account details
      const response = this.getAssociatedTokenAccount(wallet, tokenMint);

      // Get the balance
      const tokenAccountInfo = await provider.connection.getTokenAccountBalance(
        response,
      );

      return tokenAccountInfo.value.uiAmount;
    } catch (error) {
      return 0;
    }
  };

  getNativeBalance = async (publicKey: string) => {
    try {
      const provider = anchor.getProvider();
      const balance = await provider.connection.getBalance(
        toPublicKey(publicKey),
      );
      const balanceSolana = new BigNumber(balance)
        .dividedBy(LAMPORTS_PER_SOL)
        .toNumber();

      return balanceSolana;
    } catch (error) {
      return 0;
    }
  };

  getNumberOfOwnedToken = async (walletPublicKey: PublicKey) => {
    try {
      const provider = anchor.getProvider();
      // // Convert the wallet address to PublicKey
      // const walletPublicKey = new PublicKey(walletAddress);

      // Fetch all token accounts owned by the wallet
      const tokenAccounts =
        await provider.connection.getParsedTokenAccountsByOwner(
          walletPublicKey,
          {
            programId: new PublicKey(
              'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
            ), // SPL Token Program ID
          },
        );

      // Process token accounts to list balances and mint addresses
      const tokens = tokenAccounts.value
        .map((tokenAccount) => {
          const info = tokenAccount.account.data.parsed.info;
          const mint = info.mint; // Mint address (unique for each token)
          const balance = info.tokenAmount.uiAmount; // Human-readable balance

          return { mint, balance };
        })
        .filter((token) => token.balance > 0); // Filter out zero-balance tokens

      // Count unique tokens and log the results
      const uniqueTokens = new Set(tokens.map((token) => token.mint));
      console.log(`You hold ${uniqueTokens.size} unique tokens:`);
      // tokens.forEach((token) => {
      //   console.log(`Token Mint: ${token.mint}, Balance: ${token.balance}`);
      // });

      return {
        uniqueTokenCount: uniqueTokens.size,
        tokenDetails: tokens,
      };
    } catch (error) {
      console.error('Error fetching token balances:', error);
      return {
        uniqueTokenCount: 0,
        tokenDetails: [],
      };
    }
  };
  getAssociatedTokenAccount = (
    ownerPubkey: PublicKey,
    mintPk: PublicKey,
  ): PublicKey => {
    let associatedTokenAccountPubkey = PublicKey.findProgramAddressSync(
      [
        ownerPubkey.toBytes(),
        TOKEN_PROGRAM_ID.toBytes(),
        mintPk.toBytes(), // mint address
      ],
      ASSOCIATED_TOKEN_PROGRAM_ID,
    )[0];

    return associatedTokenAccountPubkey;
  };

  getConfigGlobal = async (wallet, token) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const [global_vault] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_GLOBAL)],
        program.programId,
      );
      const globalVaultTokenAccount = this.getAssociatedTokenAccount(
        global_vault,
        token,
      );
      const globalVaultBalance =
        await provider.connection.getTokenAccountBalance(
          globalVaultTokenAccount,
        );

      return globalVaultBalance;
    } catch (error) {
      console.log('getConfigGlobal error', error);
    }
  };

  getBondingAddressToken = async (wallet) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const [global_vault] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_GLOBAL)],
        program.programId,
      );

      return global_vault.toBase58();
    } catch (error) {
      console.log('error', error);
      return '';
    }
  };

  getSnapShotAgent = async () => {
    try {
      const oraiEndpoint = 'https://rpc.orai.io';
      const whitelistContractAddress =
        'orai14z64p3yp8rv99ewvycpeef7h4jlyqwmpyt63m86wyyh0dhjxhqescyclm0';
      const cwClient = await CosmWasmClient.connect(oraiEndpoint);
      const contract = new AgentsLandSnapshotContractQueryClient(
        cwClient,
        whitelistContractAddress,
      );

      // query list token metadata
      /// return:
      /// - token: token addr
      /// - metadata: base64 encode of token metadata
      const dataSnap = await contract.tokensMetadata();

      const res = (dataSnap || []).map((e) => {
        return {
          ...e,
          metadata: JSON.parse(Buffer.from(e.metadata, 'base64').toString()),
        };
      });

      return res || [];
    } catch (error) {
      console.log('error get snapshot', error);
      return [];
    }
  };

  // allowedAddresses: ConfigState['configSnapshot']
  getListTokenFromContract = async (mode: MODE_COIN) => {
    try {
      const provider = anchor.getProvider();
      const allowedAddresses = await this.getSnapShotAgent();

      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const metaplex = Metaplex.make(provider.connection);
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const tokens = await provider.connection.getParsedProgramAccounts(
        program.programId,
        {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 136,
            },
          ],
        },
      );
      const configAccount =
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig();

      const list = await Promise.all(
        tokens.map(async (item) => {
          const detail = program.coder.accounts.decode<
            anchor.IdlAccounts<PublicLaunchpad>['bondingCurve']
          >('bondingCurve', item.account.data as Buffer);

          if (
            allowedAddresses.length > 0 &&
            !allowedAddresses
              .map((e) => e.token)
              .includes(detail.tokenMint.toBase58())
          ) {
            return;
          }

          const metadata = await metaplex
            .nfts()
            .findByMint(
              { mintAddress: detail.tokenMint },
              { commitment: 'confirmed' },
            );

          let metadataJson: MetadataInfo = {} as any;
          if (metadata.uri) {
            const dataJson = (await fetchJSONDataFromUrl(metadata.uri)) || {};
            metadataJson = dataJson;
          }

          const solPrice = Number(localStorage.getItem('solPrice'));
          const newPrice = calculateTokenPrice(
            detail.reserveToken,
            detail.reserveLamport,
            configAccount.tokenDecimalsConfig || SPL_DECIMAL,
            solPrice,
          );
          const marketcap = calculateMarketCap(
            detail.reserveToken,
            configAccount.tokenDecimalsConfig || SPL_DECIMAL,
            newPrice,
          );

          const tokenDetail = {
            creator: detail.creator.toBase58(),
            decimals: configAccount.tokenDecimalsConfig || SPL_DECIMAL,
            name: metadata.name,
            ticker: metadata.symbol,
            description: metadataJson.description,
            token: detail.tokenMint.toBase58(),
            url: metadataJson.image,
            tokenReserves: detail.reserveToken,
            lamportReserves: detail.reserveLamport,
            marketcap,
            bondingCurveLimit:
              configAccount?.curveLimit || ALL_CONFIGS.MEME_BONDING_CURVE_LIMIT,
            metadata: {
              ...metadata,
              ...metadataJson,
              agentAddress: metadata.address.toBase58(),
            } as MetadataInfo,
            listed: detail.isCompleted, // TODO: this value in contract is bonding curve isCompleted, but when data BE failed we only need to check isComplete is true and user can trade via raydium
            tradingTime: new Date(
              detail.partyStart.muln(ALL_CONFIGS.TIMER.MILLISECONDS).toNumber(),
            ),
            date: new Date(
              detail.curveCreationDate
                .muln(ALL_CONFIGS.TIMER.MILLISECONDS)
                .toNumber(),
            ),
          };

          return { ...detail, ...tokenDetail };
        }),
      );

      const listFmt = list.filter(Boolean);
      return {
        coins: listFmt,
        total: listFmt.length,
        fromRpc: true,
      };
    } catch (error) {
      console.log('getListTokenFromContract error', error);
      return {
        coins: [],
        total: 0,
      };
    }
  };

  getTokenDetailFromContract = async (
    wallet: WalletContextState,
    tokenMint: PublicKey,
    mode: MODE_COIN,
  ) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const allowedAddresses = await this.getSnapShotAgent();
      const currentTokenData = allowedAddresses.find(
        (e) => e.token === tokenMint.toBase58(),
      );
      const raydiumPoolAddr =
        currentTokenData?.metadata?.['raydiumPoolAddr'] || '';
      const oraidexPoolAddr =
        currentTokenData?.metadata?.['oraidexPoolAddr'] || '';

      const metaplex = Metaplex.make(provider.connection);
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const metadata = await metaplex
        .nfts()
        .findByMint({ mintAddress: tokenMint }, { commitment: 'confirmed' });

      let metadataJson: MetadataInfo = {} as any;
      if (metadata.uri) {
        const dataJson = (await fetchJSONDataFromUrl(metadata.uri)) || {};
        metadataJson = dataJson;
      }
      const [bondingCurvePda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), tokenMint.toBytes()],
        program.programId,
      );

      const [configAccount, bondingCurve] = await Promise.all([
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig(),
        program.account.bondingCurve.fetch(bondingCurvePda),
      ]);

      const solPrice = Number(localStorage.getItem('solPrice'));
      const newPrice = calculateTokenPrice(
        bondingCurve.reserveToken,
        bondingCurve.reserveLamport,
        SPL_DECIMAL,
        solPrice,
      );
      const marketcap = calculateMarketCap(
        bondingCurve.reserveToken,
        SPL_DECIMAL,
        newPrice,
      );

      const tokenDetail = {
        creator: bondingCurve.creator.toBase58(),
        decimals: SPL_DECIMAL,
        name: metadata.name,
        ticker: metadata.symbol,
        description: metadataJson.description,
        token: tokenMint.toBase58(),
        url: metadataJson.image,
        tokenReserves: bondingCurve.reserveToken.toNumber(),
        lamportReserves: bondingCurve.reserveLamport.toNumber(),
        raydiumPoolAddr,
        oraidexPoolAddr,
        bondingCurveLimit:
          configAccount?.curveLimit || ALL_CONFIGS.MEME_INIT_SOL_BONDING_CURVE,
        metadata: {
          ...metadata,
          ...metadataJson,
          agentAddress: metadata.address.toBase58(),
        } as MetadataInfo,
        listed: raydiumPoolAddr || oraidexPoolAddr,
        tradingTime: new Date(
          toBN(bondingCurve.partyStart.toNumber())
            .multipliedBy(ALL_CONFIGS.TIMER.MILLISECONDS)
            .toNumber(),
        ),
        marketcap,
        date: new Date(
          bondingCurve.curveCreationDate
            .muln(ALL_CONFIGS.TIMER.MILLISECONDS)
            .toNumber(),
        ),
      };

      return tokenDetail;
    } catch (error) {
      console.log('getTokenDetailFromContract error', error);
      return;
    }
  };

  getConfigCurve = async (mode: MODE_COIN, onFailed?: () => void) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      if (!provider) {
        return;
      }

      const configAccount =
        mode === MODE_COIN.CTO
          ? await this.getCTOLaunchpadGlobalConfig()
          : await this.getDEGENLaunchpadGlobalConfig();

      return configAccount;
    } catch (error) {
      console.log('getConfigCurve error', error);

      errorAlert(
        'RPC call limit reached. Please wait or switch networks to continue!',
      );
      typeof onFailed === 'function' && onFailed();

      return;
    }
  };

  getCurvePDA = (tokenMint: PublicKey) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const [bondingCurvePda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), tokenMint.toBytes()],
        program.programId,
      );

      return bondingCurvePda;
    } catch (error) {
      console.log('getCurvePDA', error);
    }
  };

  decodeCurveData = (data: Buffer) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      if (!provider) {
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;
      const bondingCurveInfo = program.coder.accounts.decode<
        anchor.IdlAccounts<PublicLaunchpad>['bondingCurve']
      >('bondingCurve', data as Buffer);

      return bondingCurveInfo;
    } catch (error) {
      console.log('decodeCurveData error', error);
      return '0';
    }
  };

  async stake(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    amount: number,
    wallet: WalletContextState,
    mode: MODE_COIN,
  ) {
    let provider;
    try {
      provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const stakeIx = await program.methods
        .stake(new BN(mode), new BN(amount))
        .accounts({
          signer: wallet.publicKey,
          stakeCurrencyMint: toPublicKey(stakeCurrencyMint),
          rewardCurrencyMint: toPublicKey(rewardCurrencyMint),
        })
        .instruction();

      transaction.add(stakeIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );

        console.log('Successfully locking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  }

  async unStake(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    amount: number,
    wallet: WalletContextState,
  ) {
    let provider;
    try {
      provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const unStakeIx = await program.methods
        .destake(new BN(amount))
        .accounts({
          signer: wallet.publicKey,
          stakeCurrencyMint: toPublicKey(stakeCurrencyMint),
          rewardCurrencyMint: new PublicKey(rewardCurrencyMint),
        })
        .instruction();

      transaction.add(unStakeIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );

        console.log('Successfully unlocking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  }

  async getStakeInfoCache(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
    getNew: boolean,
  ) {
    if (!wallet.publicKey) {
      console.log('User not connected');
      return {
        bump: [],
        unstakedAtTime: new BN(0),
        stakeAmount: new BN(0),
        snapshotAmount: new BN(0),
        totalClaim: new BN(0),
        staker: wallet.publicKey,
        vault: null,
      } as anchor.IdlAccounts<PublicLaunchpad>['stakeInfo'];
    }

    // we attempt to cache stake info query since it is unlikely this data will change, unless users stake multiple times.
    // this would help reduce total # of RPC calls
    const stakeInfoLocalStorageKey = `meme-stakeInfo-${wallet.publicKey.toBase58()}-${stakeCurrencyMint}-${rewardCurrencyMint}}`;
    if (getNew) {
      return await this.getAndStoreStakeInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        wallet,
        stakeInfoLocalStorageKey,
      );
    }
    const stakeInfoJSON = localStorage.getItem(stakeInfoLocalStorageKey);
    if (!stakeInfoJSON) {
      return await this.getAndStoreStakeInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        wallet,
        stakeInfoLocalStorageKey,
      );
    }

    const parsedStakeInfo = JSON.parse(stakeInfoJSON);
    const stakeInfo: StakeInfo = {
      ...parsedStakeInfo,
      snapshotAmount: new BN(parsedStakeInfo.snapshotAmount),
      stakeAmount: new BN(parsedStakeInfo.stakeAmount),
      unstakedAtTime: new BN(parsedStakeInfo.unstakedAtTime),
    };
    return stakeInfo;
  }

  private async getAndStoreStakeInfoToLocalStorage(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
    stakeInfoLocalStorageKey: string,
  ) {
    const stakerInfo = await this.getStakeInfo(
      stakeCurrencyMint,
      rewardCurrencyMint,
      wallet,
    );
    if (!stakerInfo) {
      // fallback default value so it doesn't break the entire page
      return {
        bump: [],
        unstakedAtTime: new BN(0),
        stakeAmount: new BN(0),
        snapshotAmount: new BN(0),
        totalClaim: new BN(0),
        staker: wallet.publicKey,
        vault: null,
      } as anchor.IdlAccounts<PublicLaunchpad>['stakeInfo'];
    }
    // localStorage.setItem(
    //   stakeInfoLocalStorageKey,
    //   JSON.stringify({
    //     ...stakerInfo,
    //     totalClaim: stakerInfo.totalClaim.toString(),
    //     snapshotAmount: stakerInfo.snapshotAmount.toString(),
    //     stakeAmount: stakerInfo.stakeAmount.toString(),
    //     unstakedAtTime: stakerInfo.unstakedAtTime.toString(),
    //   }),
    // );
    return stakerInfo;
  }

  private async getStakeInfo(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
  ) {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      let [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(MEME_STAKE_VAULT_SEED),
          toPublicKey(rewardCurrencyMint).toBytes(),
        ],
        program.programId,
      );
      let [stakerInfoPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(MEME_STAKE_INFO_SEED),
          vaultPda.toBytes(),
          wallet.publicKey.toBytes(),
        ],
        program.programId,
      );
      const stakerInfo = await program.account.stakeInfo.fetch(stakerInfoPda);

      return stakerInfo;
    } catch (error) {
      console.log('Error in get stake token transaction', error, error.error);

      return;
    }
  }

  async getVaultInfoCache(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    // won't fetch vault info if we have reached the end of staking vault, and just query from local storage
    const vaultInfoKey = `reset-meme-vaultInfo-${stakeCurrencyMint}-${rewardCurrencyMint}}`;
    const vaultInfoJSON = localStorage.getItem(vaultInfoKey);
    if (!vaultInfoJSON) {
      return await this.getAndStoreVaultInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        vaultInfoKey,
      );
    }
    const parsedVaultInfo = JSON.parse(vaultInfoJSON);
    const vaultInfo: anchor.IdlAccounts<PublicLaunchpad>['stakingVault'] = {
      ...parsedVaultInfo,
      totalStaked: new BN(parsedVaultInfo.totalStaked),
      stakeEndTime: new BN(parsedVaultInfo.stakeEndTime),
      totalReward: new BN(parsedVaultInfo.totalReward),
      maxRewardPerWallet: new BN(parsedVaultInfo.maxRewardPerWallet),
    };

    const nowInSecs = Date.now() / 1000;
    // this means the vault has ended -> no need to query vault anymore
    if (
      Number(vaultInfo.stakeEndTime.toString()) > 0 &&
      nowInSecs > Number(vaultInfo.stakeEndTime.toString())
    ) {
      return vaultInfo;
    }
    // else, we update with latest vault info, and cache it into the local storage
    return await this.getAndStoreVaultInfoToLocalStorage(
      stakeCurrencyMint,
      rewardCurrencyMint,
      vaultInfoKey,
    );
  }

  private async getAndStoreVaultInfoToLocalStorage(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    vaultInfoKey: string,
  ) {
    const vaultInfo = await this.getVaultInfo(
      stakeCurrencyMint,
      rewardCurrencyMint,
    );
    const parsedVaultInfo = {
      ...vaultInfo,
      totalStaked: vaultInfo.totalStaked.toString(),
      stakeEndTime: vaultInfo.stakeEndTime.toString(),
    };

    const nowInSecs = Date.now() / 1000;
    // from party start =>> cached
    // if (nowInSecs > Number(vaultInfo.stakeEndTime.toString())) {
    //   localStorage.setItem(vaultInfoKey, JSON.stringify(parsedVaultInfo));
    // }
    return vaultInfo;
  }

  private async getVaultInfo(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    let vaultInfo: anchor.IdlAccounts<PublicLaunchpad>['stakingVault'] = {
      totalStaked: new BN('0'),
      stakeEndTime: new BN('0'),
      bump: [],
      rewardCurrencyMint: null,
      version: 1,
    };
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<PublicLaunchpad>;

      const [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(MEME_STAKE_VAULT_SEED),
          new PublicKey(rewardCurrencyMint).toBytes(),
        ],
        program.programId,
      );

      const newVaultInfo = await program.account.stakingVault.fetch(vaultPda);
      return newVaultInfo || vaultInfo;
    } catch (error) {
      console.log('Error in get stake vault info', error, error.error);
      return vaultInfo;
    }
  }
}
